import { ThemeEvents } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/** 满额达成提示的完整展示时长 */
const QUALIFY_BANNER_DURATION_MS = 5000;
/** 冻结倒计时时至少保留的可见时长，避免下次打开「闪一下就没了」 */
const MIN_QUALIFY_REMAIN_MS = 1200;

/** 当前「已满额」周期内，达成提示是否已播完 */
let qualifyBannerDismissed = false;
/** 达成提示还剩多少毫秒要展示；抽屉关闭时冻结，下次打开续上 */
let qualifyRemainingMs = QUALIFY_BANNER_DURATION_MS;
/** 达成提示计划收起的时间戳，morph 后用来续上剩余时间 */
let qualifyHideAt = 0;
let qualifyHideTimer = 0;
/** 上滑动画结束后收尾的定时器，动画中途跌回未满额时要能撤销 */
let qualifyDismissTimer = 0;

function isCartDrawerOpen() {
  const dialog = document.querySelector('cart-drawer-component dialog');
  return dialog instanceof HTMLDialogElement && dialog.open;
}

function clearQualifyTimer() {
  if (qualifyHideTimer) {
    window.clearTimeout(qualifyHideTimer);
    qualifyHideTimer = 0;
  }
  if (qualifyDismissTimer) {
    window.clearTimeout(qualifyDismissTimer);
    qualifyDismissTimer = 0;
  }
}

function resetQualifyCycle() {
  qualifyBannerDismissed = false;
  qualifyHideAt = 0;
  qualifyRemainingMs = QUALIFY_BANNER_DURATION_MS;
  clearQualifyTimer();
}

/**
 * 抽屉关闭时冻结达成提示的倒计时。
 * 计时按墙上时间走的话，抽屉关着也会照常跑完并把本周期标记成「已消费」，
 * 用户可能全程都看不到包邮达成提示。
 */
function pauseQualifyCountdown() {
  clearQualifyTimer();
  if (!qualifyHideAt) return;

  // 定时器可能刚好在抽屉关闭那一瞬跑完，冻结成 0 等于用户还是没看到，所以留一个下限
  qualifyRemainingMs = Math.max(qualifyHideAt - Date.now(), MIN_QUALIFY_REMAIN_MS);
  qualifyHideAt = 0;
}

/**
 * 同步包邮条：未满额立刻回到进度条；满额后播 5 秒再上滑；同一次满额不重播。
 */
function syncShippingProgress() {
  const root = document.querySelector('ug-cart-shipping-progress');
  if (!(root instanceof HTMLElement)) return;

  const qualified = root.dataset.qualified === 'true';
  const below = root.querySelector('[data-ug-shipping-below]');
  const achieve = root.querySelector('[data-ug-shipping-achieve]');
  const badge = document.querySelector('[data-ug-free-shipping-badge]');
  if (badge instanceof HTMLElement) {
    badge.hidden = !qualified;
  }

  if (!qualified) {
    resetQualifyCycle();
    root.classList.remove('is-dismissed', 'is-dismissing', 'is-qualified');
    below?.removeAttribute('hidden');
    achieve?.setAttribute('hidden', '');
    return;
  }

  root.classList.add('is-qualified');
  below?.setAttribute('hidden', '');

  if (qualifyBannerDismissed) {
    root.classList.add('is-dismissed');
    root.classList.remove('is-dismissing');
    achieve?.setAttribute('hidden', '');
    return;
  }

  if (!isCartDrawerOpen()) {
    pauseQualifyCountdown();
    achieve?.setAttribute('hidden', '');
    return;
  }

  achieve?.removeAttribute('hidden');
  root.classList.remove('is-dismissed');

  const now = Date.now();
  if (!qualifyHideAt) {
    qualifyHideAt = now + qualifyRemainingMs;
  }

  const remaining = qualifyHideAt - now;
  if (remaining <= 0) {
    dismissQualifyBanner(root, achieve);
    return;
  }

  clearQualifyTimer();
  qualifyHideTimer = window.setTimeout(() => {
    dismissQualifyBanner(root, achieve);
  }, remaining);
}

/**
 * @param {HTMLElement} root
 * @param {Element | null} achieve
 */
function dismissQualifyBanner(root, achieve) {
  // 抽屉已关：上滑动画根本看不见，这时把周期标记成已消费等于让用户整轮都看不到达成提示
  if (!isCartDrawerOpen()) {
    pauseQualifyCountdown();
    return;
  }

  qualifyBannerDismissed = true;
  qualifyHideAt = 0;
  clearQualifyTimer();
  root.classList.add('is-dismissing');
  // 动画这 320ms 内若跌回未满额，clearQualifyTimer 要能撤销收尾，
  // 否则 is-dismissed（display:none）会把整条进度条一起藏掉
  qualifyDismissTimer = window.setTimeout(() => {
    qualifyDismissTimer = 0;
    root.classList.remove('is-dismissing');
    root.classList.add('is-dismissed');
    achieve?.setAttribute('hidden', '');
  }, 320);
}

class UgCartShippingProgress extends HTMLElement {
  connectedCallback() {
    syncShippingProgress();
  }
}

/**
 * 打开抽屉的所有路径最终都会走 CartDrawerComponent.showDialog()（它已经调了
 * syncUgCartShippingProgress），所以这里只需要接关闭事件来冻结达成提示的倒计时。
 *
 * dialog.js 派发的 dialog:close 是默认参数的 CustomEvent（不冒泡），只能绑在
 * cart-drawer-component 元素本身；元素被 morph 的 replaceWith 换掉时监听器随元素一起释放，
 * 不会像 MutationObserver 那样累积，用 WeakSet 防重复绑定即可。
 * @type {WeakSet<Element>}
 */
const boundDrawers = new WeakSet();

function bindDrawerCloseListener() {
  const drawer = document.querySelector('cart-drawer-component');
  if (!drawer || boundDrawers.has(drawer)) return;

  boundDrawers.add(drawer);
  drawer.addEventListener(DialogCloseEvent.eventName, pauseQualifyCountdown);
}

function getAdvantageSheetParts() {
  const drawer = document.querySelector('cart-drawer-component');
  if (!(drawer instanceof HTMLElement)) return {};

  return {
    sheet: drawer.querySelector('[data-ug-advantage-sheet]'),
    titleEl: drawer.querySelector('[data-ug-advantage-sheet-title]'),
    bodyEl: drawer.querySelector('[data-ug-advantage-sheet-body]'),
  };
}

function closeAdvantageSheet() {
  const { sheet } = getAdvantageSheetParts();
  if (!(sheet instanceof HTMLElement) || !sheet.classList.contains('is-open')) return;

  // 复位展开态：卡片按钮上的 aria-expanded 由这里和 openAdvantageSheet 成对维护
  document
    .querySelectorAll('.ug-cart-advantage-card[aria-expanded="true"]')
    .forEach((btn) => btn.setAttribute('aria-expanded', 'false'));

  // 先下滑再 hidden，避免立刻 display:none 把淡出掐断
  sheet.classList.remove('is-open');
  window.setTimeout(() => {
    if (!sheet.classList.contains('is-open')) {
      sheet.setAttribute('hidden', '');
    }
  }, 320);
}

/** @param {Element | null} card */
function openAdvantageSheet(card) {
  const { sheet, titleEl, bodyEl } = getAdvantageSheetParts();
  if (!(card instanceof HTMLElement) || !sheet || !titleEl || !bodyEl) return;

  titleEl.textContent = card.dataset.advantageTitle || '';
  bodyEl.innerHTML = card.querySelector('[data-advantage-description]')?.innerHTML || '';
  card.querySelector('.ug-cart-advantage-card')?.setAttribute('aria-expanded', 'true');
  sheet.removeAttribute('hidden');
  requestAnimationFrame(() => sheet.classList.add('is-open'));
}

/* 官方变量锁横排；钱包在 Shadow DOM 里摸不到，不再查节点 */
function layoutCartAcceleratedCheckout() {
  document.querySelectorAll('.ug-cart-checkout shopify-accelerated-checkout-cart').forEach((host) => {
    if (!(host instanceof HTMLElement)) return;

    host.style.setProperty('--shopify-accelerated-checkout-button-inline-size', '44px');
    host.style.setProperty('--shopify-accelerated-checkout-button-block-size', '44px');
    host.style.setProperty('--shopify-accelerated-checkout-inline-alignment', 'space-between');
  });
}

let cartWalletObserver = null;

function watchCartAcceleratedCheckout() {
  const root = document.querySelector('.ug-cart-checkout');
  if (!(root instanceof HTMLElement)) return;

  layoutCartAcceleratedCheckout();
  if (cartWalletObserver) cartWalletObserver.disconnect();
  cartWalletObserver = new MutationObserver(() => {
    layoutCartAcceleratedCheckout();
  });
  cartWalletObserver.observe(root, { childList: true, subtree: true });
}

function bindAdvantageSheet() {
  if (document.body.dataset.ugAdvantageBound === 'true') return;

  document.body.dataset.ugAdvantageBound = 'true';
  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-ug-advantage-close]')) {
      event.preventDefault();
      closeAdvantageSheet();
      return;
    }

    const card = target.closest('[data-ug-advantage-card]');
    if (card instanceof HTMLElement) {
      event.preventDefault();
      openAdvantageSheet(card);
    }
  });
}

export function syncUgCartShippingProgress() {
  syncShippingProgress();
}

function initCartDrawerUi() {
  bindDrawerCloseListener();
  bindAdvantageSheet();
  syncShippingProgress();
  watchCartAcceleratedCheckout();
}

if (!window.__ugCartWalletResizeBound) {
  window.__ugCartWalletResizeBound = true;
  window.addEventListener('resize', () => {
    window.requestAnimationFrame(layoutCartAcceleratedCheckout);
  });
}

if (!customElements.get('ug-cart-shipping-progress')) {
  customElements.define('ug-cart-shipping-progress', UgCartShippingProgress);
}

document.addEventListener(ThemeEvents.cartUpdate, () => {
  requestAnimationFrame(initCartDrawerUi);
});
document.addEventListener(ThemeEvents.discountUpdate, () => {
  requestAnimationFrame(initCartDrawerUi);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartDrawerUi, { once: true });
} else {
  initCartDrawerUi();
}
